import {
  useAccountClient,
  JupiterTheme,
  useSnackbarNotification,
} from '@jupiterone/jupiter-web-toolkit';
import { Button, CircularProgress, TextField } from '@material-ui/core';
import { createStyles, withStyles } from '@material-ui/styles';
import React, { ChangeEvent, useState } from 'react';
import VanityDomainTextField from './VanityDomainTextField';

const styles = (theme: JupiterTheme) =>
  createStyles({
    inputSection: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
    textField: {
      marginBottom: theme.spacing(1.5),
      width: '100%',
    },
    button: {
      marginTop: theme.spacing(2),
    },
  });

interface Props {
  classes: any;
  onSubmit: VoidFunction;
}

type FormValues = {
  accountName: string;
  organizationId: string;
  email: string;
};

const AddAccountForm = ({ classes, onSubmit }: Props) => {
  const notification = useSnackbarNotification();
  const accountClient = useAccountClient();
  const [formValues, setFormValues] = useState<FormValues>({
    organizationId: '',
    accountName: '',
    email: '',
  });
  const [busy, setBusy] = useState<boolean>(false);
  const [organizationIdError, setOrganizationIdError] = useState<boolean>(
    false
  );

  const { accountName, email, organizationId } = formValues;

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    setBusy(true);

    try {
      const response = await accountClient.createAccount(
        organizationId,
        accountName,
        email
      );
      if (response.status >= 300) {
        const message = await response.json();
        throw new Error(message.error);
      }
      notification.success('Successfully created account');
      onSubmit();
    } catch (error) {
      notification.error({
        error,
        message: `Error creating account: ${error.message}`,
      });
    } finally {
      setBusy(false);
    }
  };

  const handleChangeAccountName = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    setFormValues({ ...formValues, accountName: event.target.value });
  };

  const handleChangeVanityUrl = (value: string, error: string) => {
    setFormValues({ ...formValues, organizationId: value });
    setOrganizationIdError(error !== '');
  };

  const handleChangeEmail = (event: ChangeEvent<HTMLInputElement>): void => {
    setFormValues({ ...formValues, email: event.target.value });
  };

  return (
    <form onSubmit={handleSubmit} className={classes.inputSection}>
      <TextField
        className={classes.textField}
        onChange={handleChangeAccountName}
        label="Account Name"
        required
        value={formValues.accountName}
        margin="normal"
      />
      <VanityDomainTextField
        className={classes.textField}
        fullWidth
        required
        value={organizationId}
        onChangeValue={handleChangeVanityUrl}
        margin="normal"
      />
      <TextField
        className={classes.textField}
        onChange={handleChangeEmail}
        required
        label="Owner Email"
        value={email}
        margin="normal"
      />
      <Button
        color="primary"
        variant="contained"
        disabled={busy || organizationIdError}
        type="submit"
        className={classes.button}
      >
        {busy ? <CircularProgress /> : 'Create Account'}
      </Button>
    </form>
  );
};

export default withStyles(styles)(AddAccountForm);
export { AddAccountForm as AddAccountFormUnwrapped };
